import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { Building2, ChevronRight, FileText, LogOut, Mail, MapPin, Plus } from "lucide-react";
import { accountApi, sessionApi, type Address, type AddressFields } from "../../api/account";
import { ApiError } from "../../api/client";
import { AddressForm, EMPTY_ADDRESS } from "../../components/account/AddressForm";
import { PasswordField } from "../../components/account/PasswordField";
import { LoadError } from "../../components/catalogue/Notices";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { Button, Card, Dialog, Icon, formatPH } from "../../components/ui";
import { usePageTitle } from "../catalogue/usePageTitle";
import { errorMessage, fieldError, useCustomerSession, useSetSession } from "./session";

const ADDRESSES_KEY = ["account", "addresses"] as const;

/** PRD screen 17: who is signed in, saved delivery addresses, and the password. */
export function AccountRoute() {
  usePageTitle("Your account");
  const session = useCustomerSession();
  const location = useLocation();

  if (session.isPending) return <PageWidth className="py-10 type-body text-text-muted">Loading…</PageWidth>;
  if (!session.data) {
    return (
      <PageWidth className="py-10">
        <LoadError onRetry={() => session.refetch()} />
      </PageWidth>
    );
  }
  if (!session.data.user) return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;

  const user = session.data.user;
  return (
    <Account
      email={user.email_address}
      phone={user.phone_number}
      confirmed={user.email_confirmed}
      hasPassword={user.has_password}
      company={user.company}
      passwordReset={(location.state as { passwordChanged?: boolean } | null)?.passwordChanged ?? false}
    />
  );
}

type AccountProps = { email: string | null; phone: string | null; confirmed: boolean; hasPassword: boolean; company: { name: string; discount_percent: number } | null; passwordReset: boolean };

function Account({ email, phone, confirmed, hasPassword, company, passwordReset }: AccountProps) {
  const navigate = useNavigate();
  const setSession = useSetSession();
  const signOut = useMutation({
    mutationFn: sessionApi.signOut,
    // Leave first: cleared while still here, the page would send the customer to sign in.
    onSuccess: async () => {
      await navigate("/", { replace: true });
      setSession({ user: null });
    },
  });

  return (
    <PageWidth className="max-w-[860px] pt-8">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="mb-1 type-h1 [font-size:var(--text-2xl)]">Your account</h1>
          <p className="mb-0 truncate type-body text-text-muted">
            Signed in as <span className="type-sku text-text-strong">{email ?? (phone && `+63 ${formatPH(phone)}`)}</span>
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button variant="quiet" icon={LogOut} disabled={signOut.isPending} onClick={() => signOut.mutate()}>
            Sign out
          </Button>
          {signOut.error && (
            <p role="alert" className="m-0 type-small text-danger-700">
              {errorMessage(signOut.error)}
            </p>
          )}
        </div>
      </div>
      {email && !confirmed && <ConfirmEmailNote email={email} />}
      {passwordReset && (
        <p role="status" className="mb-6 rounded-md bg-success-100 px-4 py-3 type-small text-success-700">
          Your new password is saved. You've been signed out on every other device.
        </p>
      )}
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-3">
          <Link to="/orders" className="flex min-h-14 items-center gap-3 rounded-md border border-border-subtle bg-surface-card px-5 py-3 type-body-strong text-text-strong hover:border-green-700">
            <Icon icon={FileText} size={20} className="text-ink-500" />
            <span className="flex-1">Your orders</span>
            <Icon icon={ChevronRight} size={18} className="text-ink-400" />
          </Link>
          <Link to="/corporate" className="flex min-h-14 items-center gap-3 rounded-md border border-border-subtle bg-surface-card px-5 py-3 hover:border-green-700">
            <Icon icon={Building2} size={20} className="text-ink-500" />
            <span className="flex-1">
              <span className="block type-body-strong text-text-strong">Corporate account</span>
              <span className="block type-small text-text-muted">
                {company ? `${company.name} · ${company.discount_percent}% off every part` : "For businesses: one discount on every part. Apply or see your application."}
              </span>
            </span>
            <Icon icon={ChevronRight} size={18} className="text-ink-400" />
          </Link>
        </div>
        <Addresses />
        {/* A Google or Facebook account has no password to change. */}
        {hasPassword && <ChangePassword emailed={Boolean(email)} />}
      </div>
    </PageWidth>
  );
}

/** Blocks nothing but a corporate application, which waits for a confirmed email. */
export function ConfirmEmailNote({ email }: { email: string }) {
  const resend = useMutation({ mutationFn: sessionApi.resendConfirmation });
  return (
    <div className="mb-8 flex flex-wrap items-start gap-3 rounded-md border border-gold-300 bg-surface-tint-gold p-4">
      <Icon icon={Mail} size={20} className="mt-0.5 text-gold-700" />
      <div className="min-w-0 flex-1">
        <p className="mb-1 type-body-strong text-text-strong">Confirm your email</p>
        <p className="mb-0 type-small text-text-body">
          Confirm <span className="type-sku">{email}</span> with the link we email you, so we know order and payment emails reach you.
        </p>
        {resend.isSuccess && (
          <p role="status" className="mt-2 mb-0 type-small text-success-700">
            Sent. Only the newest link works.
          </p>
        )}
        {resend.error && (
          <p role="alert" className="mt-2 mb-0 type-small text-danger-700">
            {errorMessage(resend.error)}
          </p>
        )}
      </div>
      <Button variant="outline" size="sm" disabled={resend.isPending} onClick={() => resend.mutate()}>
        {resend.isPending ? "Sending…" : "Send the link"}
      </Button>
    </div>
  );
}

function Addresses() {
  const queryClient = useQueryClient();
  const addresses = useQuery({ queryKey: ADDRESSES_KEY, queryFn: accountApi.addresses });
  // "new", an address being edited, or nothing open.
  const [editing, setEditing] = useState<"new" | Address | null>(null);
  const [removing, setRemoving] = useState<Address | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ADDRESSES_KEY });

  const save = useMutation({
    mutationFn: (fields: AddressFields) => (editing === "new" || !editing ? accountApi.createAddress(fields) : accountApi.updateAddress(editing.id, fields)),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: number) => accountApi.deleteAddress(id),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });

  const open = (target: "new" | Address) => {
    save.reset();
    setEditing(target);
  };
  const saveErrors = save.error instanceof ApiError ? save.error.details : {};
  const formError = save.error && Object.keys(saveErrors).length === 0 ? errorMessage(save.error) : undefined;
  const list = addresses.data ?? [];

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 type-h3 [font-size:var(--text-lg)]">Delivery addresses</h2>
        {editing === null && list.length > 0 && (
          <Button variant="outline" size="sm" icon={Plus} onClick={() => open("new")}>
            Add an address
          </Button>
        )}
      </div>

      {addresses.isPending ? (
        <p className="type-body text-text-muted">Loading addresses…</p>
      ) : addresses.isError ? (
        <LoadError onRetry={() => addresses.refetch()} />
      ) : (
        <div className="flex flex-col gap-3">
          {editing === "new" && (
            <Card padding="var(--space-6)">
              <h3 className="mb-4 type-h4">New address</h3>
              <AddressForm initial={EMPTY_ADDRESS} onSave={(fields) => save.mutate(fields)} onCancel={() => setEditing(null)} saving={save.isPending} errors={saveErrors} error={formError} />
            </Card>
          )}
          {list.length === 0 && editing === null && (
            <Card tone="sunken" padding="var(--space-7)" className="text-center">
              <Icon icon={MapPin} size={24} className="mx-auto mb-3 text-ink-400" />
              <p className="mb-4 type-body text-text-muted">No saved addresses yet. Save one now and checkout will offer it.</p>
              <Button icon={Plus} onClick={() => open("new")}>
                Add an address
              </Button>
            </Card>
          )}
          {list.map((address) =>
            editing !== "new" && editing?.id === address.id ? (
              <Card key={address.id} padding="var(--space-6)">
                <h3 className="mb-4 type-h4">Edit address</h3>
                <AddressForm
                  initial={{ ...address, postal_code: address.postal_code ?? "" }}
                  onSave={(fields) => save.mutate(fields)}
                  onCancel={() => setEditing(null)}
                  saving={save.isPending}
                  errors={saveErrors}
                  error={formError}
                />
              </Card>
            ) : (
              <Card key={address.id} padding="var(--space-5) var(--space-6)" className="flex flex-wrap items-start justify-between gap-4">
                <address className="min-w-0 type-body not-italic text-text-body">
                  <span className="block type-body-strong text-text-strong">{address.recipient_name}</span>
                  {address.street}, {address.barangay}
                  <br />
                  {address.city}, {address.province}
                  {address.postal_code && ` ${address.postal_code}`}
                  <span className="mt-1 block type-sku [font-size:var(--text-sm)] text-ink-600">{address.phone}</span>
                </address>
                <span className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => open(address)}>
                    Edit
                  </Button>
                  <Button variant="quiet" size="sm" onClick={() => { remove.reset(); setRemoving(address); }}>
                    Remove
                  </Button>
                </span>
              </Card>
            ),
          )}
        </div>
      )}

      <Dialog
        open={removing !== null}
        title="Remove this address?"
        onClose={() => setRemoving(null)}
        footer={
          <>
            <Button variant="quiet" onClick={() => setRemoving(null)}>
              Keep it
            </Button>
            <Button variant="danger" disabled={remove.isPending} onClick={() => removing && remove.mutate(removing.id)}>
              {remove.isPending ? "Removing…" : "Remove"}
            </Button>
          </>
        }
      >
        <p className="mb-0 type-body">
          {removing?.recipient_name}, {removing?.street}, {removing?.city}. Orders already sent there keep their address.
        </p>
        {remove.error && <p className="mt-3 mb-0 type-small text-danger-700">{errorMessage(remove.error)}</p>}
      </Dialog>
    </section>
  );
}

/** `emailed`: an account with an email can also sign in by link; a phone account resets by SMS code. */
function ChangePassword({ emailed }: { emailed: boolean }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [problem, setProblem] = useState<{ current?: string; next?: string }>({});
  const change = useMutation({
    mutationFn: () => accountApi.changePassword(current, next),
    onSuccess: () => {
      setOpen(false);
      setCurrent("");
      setNext("");
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = {
      current: current ? undefined : "Enter your current password",
      next: !next ? "Choose a new password" : next.length < 8 ? "Use at least 8 characters" : undefined,
    };
    setProblem(found);
    if (!found.current && !found.next) change.mutate();
  };

  const currentError = problem.current ?? fieldError(change.error, "current_password");
  const nextError = problem.next ?? fieldError(change.error, "password");

  return (
    <section>
      <h2 className="mb-2 type-h3 [font-size:var(--text-lg)]">Password</h2>
      {!open ? (
        <>
          <p className="mb-4 type-body text-text-muted">
            {change.isSuccess
              ? "Your new password is saved. You've been signed out on every other device."
              : emailed
                ? "You can also sign in with an emailed link, without it."
                : "Forgot it? Sign out and use “Forgot your password?”: we'll text you a code."}
          </p>
          <Button variant="outline" onClick={() => { change.reset(); setOpen(true); }}>
            Change password
          </Button>
        </>
      ) : (
        <Card padding="var(--space-6)">
          <form onSubmit={submit} noValidate className="max-w-[400px] [&>div:first-child]:mt-0">
            <PasswordField label="Current password" value={current} onChange={(value) => { setCurrent(value); setProblem({ ...problem, current: undefined }); }} autoComplete="current-password" error={currentError} />
            <PasswordField label="New password" value={next} onChange={(value) => { setNext(value); setProblem({ ...problem, next: undefined }); }} autoComplete="new-password" error={nextError} />
            {change.error && !currentError && !nextError && <p role="alert" className="mt-4 mb-0 type-small text-danger-700">{errorMessage(change.error)}</p>}
            <div className="mt-6 flex flex-wrap gap-3">
              <Button type="submit" disabled={change.isPending}>
                {change.isPending ? "Saving…" : "Save new password"}
              </Button>
              <Button type="button" variant="quiet" onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </div>
            <p className="mt-4 mb-0 type-small text-text-muted">Saving signs you out on every other device.</p>
          </form>
        </Card>
      )}
    </section>
  );
}
